import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Description as ContractIcon,
  Store as ProveedorIcon,
  ShoppingCart as CompraIcon,
  Inventory as InventoryIcon,
  Assignment as ParteIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const AdminDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const quickStats = [
    { title: 'Facturas Pendientes', value: '18', icon: <ReceiptIcon />, color: theme.palette.warning.main },
    { title: 'Contratos Activos', value: '32', icon: <ContractIcon />, color: theme.palette.success.main },
    { title: 'Proveedores', value: '85', icon: <ProveedorIcon />, color: theme.palette.info.main },
    { title: 'OC Abiertas', value: '12', icon: <CompraIcon />, color: theme.palette.primary.main },
  ];

  const ordenesEstado = [
    { name: 'Pendiente', value: 4 },
    { name: 'Aprobada', value: 5 },
    { name: 'En Proceso', value: 3 },
  ];

  const pieColors = [theme.palette.warning.main, theme.palette.success.main, theme.palette.info.main];

  const facturacionMensual = [
    { mes: 'Oct', monto: 35 },
    { mes: 'Nov', monto: 42 },
    { mes: 'Dic', monto: 58 },
    { mes: 'Ene', monto: 45 },
  ];

  const alertas = [
    { texto: 'Stock bajo: Tuberías 4"', tipo: 'Stock', criticidad: 'Alta' },
    { texto: 'Contrato #CT-2024-015 vence en 15 días', tipo: 'Contrato', criticidad: 'Media' },
    { texto: '3 facturas vencidas sin pagar', tipo: 'Factura', criticidad: 'Alta' },
    { texto: 'OC #1234 pendiente de aprobación', tipo: 'OC', criticidad: 'Media' },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Administración
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestión administrativa y compras
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<CompraIcon />} onClick={() => navigate('/admin/ordenes-compra')}>
          Nueva Orden de Compra
        </Button>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {quickStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Órdenes de Compra por Estado */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Órdenes de Compra
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={ordenesEstado}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={70}
                    dataKey="value"
                  >
                    {ordenesEstado.map((entry, index) => (
                      <Cell key={index} fill={pieColors[index]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                {ordenesEstado.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: pieColors[index] }} />
                    <Typography variant="caption">{item.name}: {item.value}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Facturación Mensual */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Facturación (Millones ARS)
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={facturacionMensual}>
                  <XAxis dataKey="mes" />
                  <YAxis />
                  <Tooltip formatter={(value) => `$${value}M`} />
                  <Bar dataKey="monto" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Alertas */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Alertas
              </Typography>
              <List disablePadding>
                {alertas.map((alerta, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      mb: 1,
                      bgcolor: alpha(alerta.criticidad === 'Alta' ? theme.palette.error.main : theme.palette.warning.main, 0.05),
                    }}
                  >
                    <WarningIcon sx={{ color: alerta.criticidad === 'Alta' ? theme.palette.error.main : theme.palette.warning.main, mr: 1.5, fontSize: 20 }} />
                    <ListItemText
                      primary={alerta.texto}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                    <Chip
                      label={alerta.tipo}
                      size="small"
                      color={alerta.criticidad === 'Alta' ? 'error' : 'warning'}
                      variant="outlined"
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
